import React, { useEffect, useState } from 'react';
import { AppState, Platform, View, Text, TouchableOpacity } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { isPushLikelyDeliverable, subscribeToPushStateChanges } from '@/lib/onesignal-push';
import { useSupabase } from '@/hooks/useSupabase';
import { useCrew } from '@/context/CrewProvider';
import { trpc } from '@/trpc/react';
import { getBiometricInlinePhrase } from '@/lib/biometric';
import colors from '@/constants/Colors';

// Module-scoped flag — once dismissed, stays hidden until the JS runtime
// restarts (i.e. a fresh cold launch). Lets us nudge the user gently without
// turning into a permanent setting we have to manage.
let dismissedThisSession = false;

export default function BiometricSetupBanner() {
  if (Platform.OS === 'web') return null;

  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { biometricAvailable, biometricEnabled, biometricLabel } = useSupabase();
  const { currentCrewMember } = useCrew();
  const [dismissed, setDismissed] = useState<boolean>(dismissedThisSession);
  const [pushOk, setPushOk] = useState<boolean>(isPushLikelyDeliverable);

  // Defer to the unread-messages banner if it'll be showing — same idea as
  // the push-banner deferral below: we never want two banners stacked.
  // React Query dedupes this with the call in unread-messages-banner.tsx.
  const { data: unreadCount = 0 } = trpc.messages.getUnreadMessageCount.useQuery(
    { crew_id: currentCrewMember?.id! },
    {
      enabled: !!currentCrewMember?.id,
      refetchInterval: 30000,
    },
  );

  useEffect(() => {
    const refresh = () => setPushOk(isPushLikelyDeliverable());
    const unsubscribe = subscribeToPushStateChanges(() => refresh());
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') refresh();
    });
    return () => {
      unsubscribe();
      sub.remove();
    };
  }, []);

  if (!biometricAvailable || biometricEnabled || dismissed) return null;
  // Defer to higher-priority banners (push > unread messages > biometric)
  // so we never stack two top banners.
  if (!pushOk) return null;
  if (unreadCount > 0) return null;

  const handleSetup = () => {
    try {
      router.push({
        pathname: '/(app)/settings',
        params: { tab: 'access', promptEnable: '1' },
      });
    } catch {
      // If the route isn't reachable for any reason, just dismiss the banner.
      handleDismiss();
    }
  };

  const handleDismiss = () => {
    dismissedThisSession = true;
    setDismissed(true);
  };

  return (
    <TouchableOpacity
      onPress={handleSetup}
      activeOpacity={0.85}
      accessibilityRole="button"
      accessibilityLabel={`Set up quick sign-in with ${getBiometricInlinePhrase(biometricLabel)}`}
      style={{
        width: '100%',
        backgroundColor: '#1E1E1E',
        paddingTop: insets.top,
        borderBottomColor: 'rgba(255,255,255,0.08)',
        borderBottomWidth: 1,
      }}
    >
      <View
        style={{
          paddingHorizontal: 16,
          paddingVertical: 10,
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <View
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            backgroundColor: 'rgba(255,240,0,0.12)',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 12,
          }}
        >
          <Ionicons
            name={
              biometricLabel === 'Face ID' || biometricLabel === 'face unlock'
                ? 'scan-outline'
                : 'finger-print-outline'
            }
            size={18}
            color={colors.accent}
          />
        </View>

        <View style={{ flex: 1, paddingRight: 12 }}>
          <Text style={{ color: '#fff', fontWeight: '600', fontSize: 14 }} numberOfLines={1}>
            Sign in faster next time
          </Text>
          <Text
            style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}
            numberOfLines={2}
          >
            Skip typing your password by signing in with {getBiometricInlinePhrase(biometricLabel)}.
          </Text>
        </View>

        <View
          style={{
            backgroundColor: colors.accent,
            paddingHorizontal: 12,
            paddingVertical: 6,
            borderRadius: 6,
            marginRight: 4,
          }}
        >
          <Text style={{ color: '#000', fontWeight: '700', fontSize: 12 }}>Set up</Text>
        </View>

        <TouchableOpacity
          onPress={handleDismiss}
          accessibilityRole="button"
          accessibilityLabel="Dismiss"
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          style={{ padding: 4 }}
        >
          <Ionicons name="close" size={20} color="rgba(255,255,255,0.6)" />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );
}
